import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle
} from "react";
import PropTypes from "kf-proptypes/widgets";

import { useUserStore } from "base/stores";
import {
  dateFormatString,
  dateTimeFormatString,
  formatToUserTimeZone,
  getTimeZoneAbbr,
  formatToLocale
} from "base/utils";
import { useHideOnScroll } from "base/hooks";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE,
  ICON_HOVER_TYPE,
  ICON_PADDING
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { getTimeZoneDescription } from "@widgets/common/libs/util.js";

import styles from "./datetimepicker.css";

export const PICKERINPUT_SIZE = {
  SMALL_DEPRECATED: "smallDeprecated",
  SMALLPLUS_DEPRECATED: "smallplusDeprecated",
  BASE: "base",
  MEDIUM: "medium",
  SMALL: "small"
};

export const PICKERINPUT_TYPE = {
  NORMAL: "normal",
  MINIMAL: "minimal"
};

export function PickerInputComponent(props, ref) {
  let {
    readOnly,
    disabled,
    includeTime,
    onClick,
    onFocus,
    dateString,
    autoFocus,
    onChange,
    type = PICKERINPUT_TYPE.NORMAL,
    size = PICKERINPUT_SIZE.BASE,
    isError,
    className,
    customTimeZone,
    isHoverMode = false,
    showTimeZoneDescription,
    hideOnScroll = true
  } = props;

  const [isShowDescriptionInHoverMode, setIsShowDescriptionInHoverMode] =
    useState(false);
  const [isShowDescription, setIsShowDescription] = useState(false);
  const [inputValue, setInputValue] = useState(dateString);
  const [openState, setOpenState] = useState(false);
  const inputRef = useRef(null);
  const { currentUser } = useUserStore.getState();
  const userTimezone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale =
    (currentUser && currentUser.DateFormat) ||
    Intl.DateTimeFormat().resolvedOptions().locale;

  useEffect(
    function initEffect() {
      if (dateString) {
        if (includeTime) {
          const convetedDateTime = formatToUserTimeZone(dateTime[0]);

          setInputValue(convetedDateTime);
        } else {
          let convertedDate = formatToLocale(dateString);
          setInputValue(convertedDate);
        }
      } else {
        setInputValue("");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateString]
  );

  useHideOnScroll({
    hideOnScroll,
    className: "ant-select-dropdown",
    setState: () => {
      inputRef.current && inputRef.current.blur();
      setOpenState(false);
    }
  });

  let dateTime = dateString && dateString.split(" ");

  let timeZone = customTimeZone ? customTimeZone : dateTime && dateTime[1];

  let currentTimezone = customTimeZone ? customTimeZone : userTimezone;
  let currentTimezoneAbbr = getTimeZoneAbbr(currentTimezone);
  let timezoneAbbr;
  let TimeZoneDescrption;
  const format = includeTime
    ? dateTimeFormatString(locale)
    : dateFormatString(locale);

  if (timeZone) {
    timezoneAbbr = getTimeZoneAbbr(timeZone);
    TimeZoneDescrption = getTimeZoneDescription(dateTime[0], locale, timeZone);
  }

  const handleShowDescription = function handleShowDescription(value = false) {
    if (timezoneAbbr) {
      if (
        !(timezoneAbbr === currentTimezoneAbbr || !includeTime) &&
        isHoverMode
      )
        setIsShowDescriptionInHoverMode(value);
    }
  };

  const onBlur = function onBlur(e) {
    setOpenState(false);
  };

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current.focus();
        inputRef.current.click();
      },
      blur: () => {
        inputRef.current.blur();
      }
    };
  });

  const clearValue = function clearValue(e) {
    e.stopPropagation();
    setInputValue("");
    onChange("");
  };

  useEffect(
    function initEffect() {
      if (timezoneAbbr && !isHoverMode) {
        if (timezoneAbbr === currentTimezoneAbbr || !includeTime) {
          setIsShowDescription(false);
        } else {
          setIsShowDescription(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timezoneAbbr]
  );

  return (
    <div className={styles.mainContainer}>
      <div
        className={`${styles.dateTimePickerInput} ${className}`}
        onMouseLeave={() => {
          handleShowDescription(false);
        }}
      >
        <div
          className={`${readOnly ? styles.readOnlyField : ""} 
          ${styles.dateTimePicker} ${styles[size]}
          ${openState ? styles.pickerOpenState : ""} 
          ${styles[type]} 
          ${disabled ? styles.disabled : ""} 
          ${isError ? styles.errorStyle : ""} 
          pickerinput-inner-container`}
          onMouseEnter={() => {
            handleShowDescription(true);
          }}
          onClick={() => {
            handleShowDescription(true);
          }}
        >
          <div
            className={`${styles.datePickerInput}`}
            onClick={() => {
              !readOnly && setOpenState(true);
              !readOnly && onClick();
            }}
            onBlur={onBlur}
            tabIndex="0"
          >
            <input
              type="text"
              value={inputValue || ""}
              readOnly
              onFocus={onFocus}
              autoFocus={autoFocus}
              ref={inputRef}
              placeholder={!disabled && !readOnly && format}
            />
            {!(readOnly || disabled) && type === PICKERINPUT_TYPE.NORMAL && (
              <React.Fragment>
                {inputValue && !disabled && (
                  <KfIcon
                    className={`${styles.closeIcon}`}
                    onClick={clearValue}
                    icon={KF_ICONS.CLOSE}
                    stroke={ICON_STROKE.REGULAR}
                    size={ICON_SIZE.SIZE_8}
                    color="--kf-color-gray-700"
                    hoverType={ICON_HOVER_TYPE.LIGHT_GRAY}
                    padding={ICON_PADDING.PADDING_4}
                  />
                )}
                {includeTime ? (
                  <KfIcon
                    icon={KF_ICONS.FORM_FIELD_DATE_TIME}
                    stroke={ICON_STROKE.THIN}
                    size={ICON_SIZE.SIZE_16}
                    color="--kf-color-gray-700"
                  />
                ) : (
                  <KfIcon
                    icon={KF_ICONS.FORM_FIELD_DATE}
                    stroke={ICON_STROKE.THIN}
                    size={ICON_SIZE.SIZE_16}
                    color="--kf-color-gray-700"
                  />
                )}
              </React.Fragment>
            )}
          </div>
        </div>
        {isShowDescriptionInHoverMode && dateString && (
          <div
            className={styles.timezonePopup}
            onMouseEnter={() => {
              handleShowDescription(true);
            }}
          >
            <KfIcon
              icon={KF_ICONS.CLOCK}
              stroke={ICON_STROKE.THIN}
              size={ICON_SIZE.SIZE_8}
              color="--kf-color-gray-700"
            />
            <div className={`${styles.timeZonetext} tiny`}>
              {TimeZoneDescrption}
            </div>
          </div>
        )}
      </div>

      {isShowDescription &&
        showTimeZoneDescription &&
        dateString &&
        TimeZoneDescrption && (
          <div className={`${styles.timeZoneContainer} `}>
            <KfIcon
              icon={KF_ICONS.CLOCK}
              stroke={ICON_STROKE.THIN}
              size={ICON_SIZE.SIZE_8}
              color="--kf-color-gray-700"
            />
            <div className={`${styles.timeZonetext} tiny`}>
              {TimeZoneDescrption}
            </div>
          </div>
        )}
    </div>
  );
}

PickerInputComponent.propTypes = {
  readOnly: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
    PropTypes.object
  ]),
  includeTime: PropTypes.bool,
  onClick: PropTypes.func,
  dateString: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  autoFocus: PropTypes.bool,
  type: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  isError: PropTypes.bool,
  className: PropTypes.string,
  customTimeZone: PropTypes.string,
  isHoverMode: PropTypes.bool,
  hideOnScroll: PropTypes.bool,
  showTimeZoneDescription: PropTypes.bool
};

export const PickerInput = forwardRef(PickerInputComponent);
